import { z } from 'zod';

import { NO_POS } from '../ast.ts';
import type { EvalResult } from '../env.ts';
import { resolveSymbolWithSessionFallthrough } from '../env.ts';
import {
  NestedDatasetCellError,
  ShapeError,
  TypeMismatchError,
  UnboundSymbolError,
  UnknownColumnRefError,
  UnknownFieldRefError,
} from '../errors.ts';
import { type AstInput, astShape, embeddedNumber, embeddedString, nodePos, opCallPos } from '../normalize.ts';
import { opMeta, type RowFn, type SpecialCtx } from '../op-meta.ts';
import { paramPath } from '../path.ts';
import { astArg } from '../schema/ast-codec.ts';
import { type ColumnType, describeColumnType, unwrapNullable, walkableFields, walkFieldType } from '../schema/types.ts';
import { type Cell, ColumnRef, Dataset, describeValueKind, isUnitCell, MapValue, type Value } from '../value.ts';

/**
 * `(col target name [path…])` — polymorphic accessor. The first arg is
 * resolved to a Value at apply time; dispatch on the resolved kind:
 *
 *   - **Dataset** → returns a `ColumnRef` for the named column,
 *     validating that the column (and any sub-path — struct fields, or
 *     a relation's implicit `{space, externalId}`) exists on the
 *     source schema. Missing column → `UnknownColumnRefError`;
 *     sub-path walk into an unwalkable type → `TypeMismatchError`.
 *   - **ColumnRef** → returns a new `ColumnRef` that extends the
 *     source ref's struct path: the column name slot becomes a new
 *     path segment, the supplied path segments follow.
 *   - **MapValue** → walks the keys (column + path) through nested
 *     maps, returning the leaf value. Missing keys produce `null`.
 *
 * A bare-symbol first arg is resolved via env → binding-scope fallthrough
 * (cross-turn re-derivation). All other first-arg shapes are evaluated
 * eagerly via `ctx.evalNode`. Column-name and path args accept a keyword,
 * a symbol, or a string literal — all three produce the same label.
 *
 * The keyword-as-function surface `(:k target …)` is parser-level sugar:
 * it desugars to `(col target :k …)` at parse time and dispatches
 * identically.
 */
export const colOpForm = z
  .object({
    name: z.literal('col'),
    params: z.array(astArg()).min(2, {
      error:
        '`(col …)` needs at least 2 arguments — `(col <dataset|binding> <column> [<path>…])` — because it extracts a COLUMN, not a dataset. To pass a whole bound dataset (e.g. as the input to `transform`) reference it by its bare binding name, not `(col <name>)`.',
    }),
  })
  .describe(
    'Polymorphic accessor. `(col target name [path…])` — dispatches on ' +
      'the resolved target: Dataset → ColumnRef; ColumnRef → ColumnRef ' +
      'with extended struct path; Map → leaf value at the key chain. ' +
      'The sugar form `(:name target)` desugars to `(col target :name)`.',
  )
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: [
      { expr: '(col wells :revenue)' },
      { expr: '(:revenue wells)' },
      { expr: '(col wells :location :coords :lat)' },
    ],
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const [first, second, ...rest] = data.params as readonly unknown[];
      const callPos = opCallPos(ctx.spans, ctx.path);

      // Resolve the first arg to a Value (bare-symbol → env lookup;
      // anything else → eager eval).
      const target = await resolveColTarget(first as AstInput, ctx, paramPath(ctx.path, 0), callPos);

      // Column name + path labels are name literals: keyword, symbol, or
      // string. extractColumnLabel rejects expression-shaped args.
      const column = extractColumnLabel(second, ctx.spans, paramPath(ctx.path, 1), 'column name');
      const path = rest.map((p, i) =>
        extractColumnLabel(p, ctx.spans, paramPath(ctx.path, 2 + i), `path segment ${i}`),
      );

      // Apply requires the head to resolve to a walkable Value (Dataset
      // / ColumnRef / Map). The walk itself is `walkValue` — the SSOT
      // shared with the per-row expression path.
      if (!(target instanceof Dataset || target instanceof ColumnRef || target instanceof MapValue)) {
        throw new TypeMismatchError(
          describeValueKind(target),
          'a Dataset, ColumnRef, or Map',
          '(col …) first arg',
          callPos,
        );
      }
      const value = walkValue(target, [column, ...path], callPos) as Value;
      // Tag a fresh dataset-column ref with the binding it was read from
      // (the head symbol), so the dashboard single-dataset guard can group
      // a tile's columns by their source dataset. Only a bare-symbol head
      // over a Dataset yields a nameable source; path-extension refs and
      // expression heads carry none.
      const headShape = astShape(first as AstInput);
      const source = headShape.kind === 'symbolRef' ? headShape.sym : undefined;
      const tagged =
        source !== undefined && target instanceof Dataset && value instanceof ColumnRef
          ? new ColumnRef(value.ds, value.column, value.path, source)
          : value;
      return { value: tagged, env: ctx.env };
    },
    // Expression context (`derive` body / `filter` predicate): the
    // head (`first`) is resolved through the normal compile/scope
    // machinery — `%` (and `%1`/`%2`) is the per-row param bound by the
    // enclosing lambda, a nested `(col …)` head is compiled recursively
    // (so `(col (:rel %) :x)` works), and a bound name resolves via env.
    // `col` itself has NO knowledge of rows or `%`: it resolves its
    // head and walks the rest via `walkValue`. Foreign cross-dataset
    // refs are inlined upstream (`inlineSetRefs`) and never reach here. A genuinely unbound head fails fast with
    // `UnboundSymbolError`; a missing sub-field on the resolved
    // row fails with `UnknownFieldRefError` — the same diagnostics the
    // static type-check surfaces earlier when the schema is known.
    // (No `inferType`: the `InferFn` contract has no schema access, so
    // the column's struct-field type is conservatively `unknown`.)
    expr: {
      compileExpr: async (call, compile, path): Promise<RowFn> => {
        const params = (call.params ?? []) as readonly AstInput[];
        const [first, ...labelArgs] = params;
        // Column + path labels are name literals (keyword / symbol /
        // string). Only the HEAD may be an arbitrary expression.
        const labels = labelArgs.map((p) => {
          const ps = astShape(p);
          if (ps.kind === 'keyword') {
            return ps.name;
          }
          if (ps.kind === 'symbolRef') {
            return ps.sym;
          }
          const label = embeddedString(p);
          if (label !== undefined) {
            return label;
          }
          // An integer label is the classic "index into the array" mistake — `col` paths
          // are field projections, never positional indexes. Steer to field-projection / `unroll`.
          if (embeddedNumber(p) !== undefined) {
            throw new ShapeError(
              '`(col …)` path labels are field names, not array indexes — an integer index like this cannot ' +
                'index a relation list / `array<struct>` positionally. Name the field instead: `(col % assets name)` ' +
                'MAPS the field across every element and returns an `array<…>`. To get one row per element, `unroll` first.',
              NO_POS,
            );
          }
          throw new ShapeError(
            '`(col …)` column/path labels must be keywords, symbols, or string literals. To read a field off a ' +
              'relation list / `array<struct>`, name it — `(col % assets name)` maps it across all elements → an array.',
            NO_POS,
          );
        });
        const headShape = astShape(first as AstInput);
        const headSym = headShape.kind === 'symbolRef' ? headShape.sym : undefined;
        const headFn = await compile(first as AstInput, paramPath(path, 0));
        return (row) => {
          const target = headFn(row) as Value | Cell | undefined;
          // A bare-symbol head that resolved to neither a scope binding
          // nor a column of the current row is a genuine unbound reference.
          if (target === undefined && headSym !== undefined && !Object.hasOwn(row, headSym)) {
            throw new UnboundSymbolError(headSym, Object.keys(row), NO_POS);
          }
          return walkValue(target ?? null, labels, NO_POS) as unknown;
        };
      },
    },
  });

/**
 * Resolve `(col …)`'s first arg to a Value. A bare-symbol first arg
 * resolves via env → binding-scope fallthrough (cross-turn re-derivation
 * via `resolveSymbolWithSessionFallthrough`); any other AST shape is
 * evaluated eagerly via `ctx.evalNode`. The Value's runtime kind drives
 * the polymorphic dispatch in `colOpForm`'s apply (Dataset / ColumnRef
 * / MapValue).
 */
const resolveColTarget = async (
  first: AstInput,
  ctx: SpecialCtx,
  firstPath: string,
  callPos: ReturnType<typeof opCallPos>,
): Promise<Value> => {
  const shape = astShape(first);
  if (shape.kind === 'symbolRef') {
    return resolveSymbolWithSessionFallthrough(ctx.env, ctx.evalCtx.bindings, shape.sym, callPos);
  }
  const { value } = await ctx.evalNode(first, ctx.env, firstPath);
  return value as Value;
};

/**
 * Extract a column-label (column name or path segment) from a node
 * arg. Accepts a keyword (`(col ds :revenue)`), a symbol (`(col ds
 * revenue)`), or a string literal (`(col ds "revenue")` — for
 * arbitrary-character names). All three produce the same `'revenue'`
 * label. Anything else (number, vector, op-call, …) is a shape error
 * because column labels are name literals, not expressions.
 */
const extractColumnLabel = (
  node: unknown,
  spans: ReturnType<typeof opCallPos> extends ReturnType<infer SignatureType> ? Parameters<SignatureType>[0] : never,
  path: string,
  role: string,
): string => {
  const shape = astShape(node);
  if (shape.kind === 'keyword') {
    return shape.name;
  }
  if (shape.kind === 'symbolRef') {
    return shape.sym;
  }
  const label = embeddedString(node);
  if (label !== undefined) {
    return label;
  }
  throw new ShapeError(`expected a keyword, symbol, or string literal as the ${role} of \`col\``, nodePos(spans, path));
};

/**
 * The walkable-subfield map of a (possibly nullable) type, descending THROUGH
 * `array` wrappers — so `array<struct>` / `array<hydrated-relation>` expose the
 * ELEMENT's subfields, which `(col % arr field)` then MAPS across the elements
 * (see {@link walkFieldType}). `undefined` for a type with no walkable subfields
 * anywhere down the array chain. Used only to surface the "did you mean" sibling
 * keys + the no-walkable-subfields diagnostic; the resolved field TYPE comes
 * from {@link walkFieldType} (which re-wraps the array).
 */
const walkableFieldsDeep = (t: ColumnType): Readonly<Record<string, ColumnType>> | undefined => {
  const u = unwrapNullable(t);
  return u.kind === 'array' ? walkableFieldsDeep(u.element) : walkableFields(u);
};

/**
 * Validate a `(col ...)` reference against the source Dataset's
 * schema. Three failure modes: column not in schema (raises
 * `UnknownColumnRefError`); path tries to walk into a type with no
 * walkable subfields (raises `TypeMismatchError`); path segment isn't
 * a known subfield of the current type (raises `UnknownFieldRefError`).
 * Subfield-ness is the `walkableFields` SSOT: struct fields, a relation's
 * implicit `{space, externalId}` instance-identity fields, a hydrated
 * relation's identity axis PLUS its target view's fields, or — for an
 * `array<…>` of any of those — the element's subfields, MAPPED across the
 * array (yielding `array<fieldType>`).
 *
 * `nullable(...)` wrappers are unwrapped transparently — refs into a
 * nullable struct column behave the same as refs into the underlying
 * struct. Null-cell semantics are a runtime concern of the consumer,
 * not a typecheck concern here.
 */
export const validateColumnPath = (
  ds: Dataset,
  column: string,
  path: readonly string[],
  pos: ReturnType<typeof opCallPos>,
): void => {
  const colType = ds.schema.get(column);
  if (colType === undefined) {
    throw new UnknownColumnRefError(column, Array.from(ds.schema.keys()), pos);
  }
  path.reduce<ColumnType>((curType, segment) => {
    const fields = walkableFieldsDeep(curType);
    if (fields === undefined) {
      throw new TypeMismatchError(
        `a column of ColumnType \`${describeColumnType(curType)}\``,
        'a column with walkable subfields (a struct, or a relation reference)',
        `(col ... ${segment})`,
        pos,
      );
    }
    if (fields[segment] === undefined) {
      throw new UnknownFieldRefError(segment, Object.keys(fields), pos);
    }
    // `walkFieldType` re-wraps the array, so a field walked off `array<struct>`
    // advances as `array<fieldType>` (MAP), keeping nested array walks correct.
    return walkFieldType(curType, segment) as ColumnType;
  }, unwrapNullable(colType));
};
/**
 * Strict per-cell key walk over a row record / struct cell. A missing
 * key on a record is a typo (`UnknownFieldRefError` with the sibling
 * keys as the "did you mean"); a `null` short-circuits to `null`; an
 * ARRAY cell MAPS the remaining labels across its elements (so
 * `(col % arr field)` projects `field` off each element → `array<field>`,
 * matching the static `array<struct>` projection); a sub-path into a
 * scalar yields `null` (path-name typos only); a `Dataset` cell is an
 * illegal nested dataset (`NestedDatasetCellError`).
 */
const walkRecordStrict = (start: Cell, labels: readonly string[], pos: ReturnType<typeof opCallPos>): Cell => {
  if (labels.length === 0) {
    if (start instanceof Dataset) {
      throw new NestedDatasetCellError('', pos);
    }
    return start ?? null;
  }
  if (start == null) {
    return null;
  }
  if (start instanceof Dataset) {
    throw new NestedDatasetCellError(labels[0] as string, pos);
  }
  if (Array.isArray(start)) {
    return start.map((element) => walkRecordStrict(element as Cell, labels, pos)) as Cell;
  }
  // A unit-boxed number is a SCALAR (the box is not a walkable record):
  // a deeper sub-path yields `null`, exactly like a plain number.
  if (isUnitCell(start)) {
    return null;
  }
  const [seg, ...rest] = labels;
  if (typeof start === 'object') {
    if (!Object.hasOwn(start, seg as string)) {
      throw new UnknownFieldRefError(seg as string, Object.keys(start), pos);
    }
    const next = (start as Record<string, Cell>)[seg as string] ?? null;
    // A field that resolves to a nested Dataset cell can't be projected
    // further by a col path — report it by the segment we just walked, so
    // the error names the column rather than the anonymous leaf.
    if (next instanceof Dataset) {
      throw new NestedDatasetCellError(seg as string, pos);
    }
    return walkRecordStrict(next, rest, pos);
  }
  return null;
};

/**
 * The single generic `(col …)` walker, shared by `colOpForm`'s apply
 * (a resolved Value target) and its expression compile (a per-row
 * record target). The target's runtime kind picks the walk — there is
 * NO name-shape heuristic and NO row-vs-foreign classification here:
 * that was decided by how the head resolved in scope, upstream. The
 * `labels` are everything after the head (`[column, ...path]`).
 *
 *   - Dataset    → a schema-validated `ColumnRef` for `labels[0]` with
 *                  struct sub-path `labels[1…]`.
 *   - ColumnRef  → a `ColumnRef` extending the source ref's path with
 *                  every label.
 *   - MapValue   → lenient key walk (a `{:k v}` literal map; a missing
 *                  key yields `null`).
 *   - record / StructCell / scalar → strict key walk (see
 *                  {@link walkRecordStrict}).
 */
const walkValue = (
  target: Value | Cell,
  labels: readonly string[],
  pos: ReturnType<typeof opCallPos>,
): Value | Cell => {
  if (target instanceof Dataset) {
    const [column, ...path] = labels as readonly [string, ...string[]];
    validateColumnPath(target, column, path, pos);
    return new ColumnRef(target, column, path);
  }
  if (target instanceof ColumnRef) {
    return new ColumnRef(target.ds, target.column, [...target.path, ...labels]);
  }
  if (target instanceof MapValue) {
    return labels.reduce<Value>(
      (acc, key) => (acc instanceof MapValue ? (acc.entries.get(key) ?? null) : null),
      target,
    );
  }
  return walkRecordStrict(target as Cell, labels, pos);
};
